<template>
  <v-form ref="createEstimateRepairerForm" v-model="formValid" class="rm-drawer-step">
    <div class="rm-drawer-scroll">
      <section class="drawer-section">
        <div class="drawer-field">
          <p required="true" class="custom-label">{{ $t('ESTIMATE_REPAIRER.ATTACHMENT') }}</p>
          <v-btn-secondary
            class="w-100"
            :disabled="attachmentModalPending"
            prepend-icon="mdi-paperclip"
            variant="flat"
            @click="openRepairerEstimateAttachmentModal"
          >
            {{ repairerEstimateAttachmentLabel }}
          </v-btn-secondary>
          <InfoAlert class="mt-2" :message="$t('ESTIMATE_REPAIRER.ATTACHMENT_INFO')" />
          <InfoAlert
            v-if="isUnderGuarantee"
            class="mt-2"
            color="warning"
            icon="mdi-alert"
            :message="$t('ESTIMATE_REPAIRER.GUARANTEE_WARNING')"
          />
          <v-chip
            v-if="hasRepairerEstimateAttachment"
            class="uploaded-chip mt-2"
            color="default"
            variant="tonal"
          >
            {{
              currentRepairFile.repairer_estimate_filename ||
              $t('ESTIMATE_REPAIRER.ATTACHMENT_UPLOADED')
            }}
          </v-chip>
        </div>

        <div v-if="hasRepairerEstimateAttachment" class="drawer-field">
          <p required="true" class="custom-label">
            {{ $t('ESTIMATE_REPAIRER.REPAIRER_NUMBER') }}
          </p>
          <v-text-field
            v-model="repairerNumber"
            :placeholder="$t('ESTIMATE_REPAIRER.PLACEHOLDER.REPAIRER_NUMBER')"
            :rules="requiredRules"
            :error-messages="error?.repairerNumber"
            :maxlength="15"
            hide-details="auto"
          />
          <p
            v-html="$t('ESTIMATE_REPAIRER.HINT.REPAIRER_NUMBER').replace(/\n/g, '<br>')"
            class="custom-hint"
          />
        </div>
      </section>
    </div>
  </v-form>
</template>

<script>
import RepairFileService from '@/services/RepairFileService'
import ResponseConstant from '@/constants/responseConstant'
import { handleResponseError } from '@/utils/toastUtils'
import Validators from '@/utils/validators'
import AttachmentUploadModal from '@/components/ui/AttachmentUploadModal.vue'
import { createRepairProofUploadAdapter } from '@/services/attachments/repairUploadAdapters'
import { displayModal } from '@/utils/modalUtils'
import InfoAlert from '@/components/ui/InfoAlert.vue'
import RepairFileGuaranteeConstant from '@/constants/repair-module/repair-file/repairFileGuaranteeConstant'

const REPAIRER_ESTIMATE_ATTACHMENT_TYPE = 'REPAIRER_ESTIMATE'

export default {
  components: { InfoAlert },
  props: {
    currentRepairFile: Object
  },
  data() {
    return {
      formValid: false,
      loading: false,
      attachmentModalPending: false,
      error: null,
      repairerNumber: '',
      requiredRules: Validators.requiredRules
    }
  },
  computed: {
    isUnderGuarantee() {
      return [
        RepairFileGuaranteeConstant.SERIAL_NUMBER,
        RepairFileGuaranteeConstant.COMMERCIAL,
        RepairFileGuaranteeConstant.EXTENSION,
        RepairFileGuaranteeConstant.FORCE_GUARANTEE
      ].includes(this.currentRepairFile?.status_guarantee)
    },
    hasRepairerEstimateAttachment() {
      return Boolean(this.currentRepairFile?.repairer_estimate_file_path)
    },
    repairerEstimateAttachmentLabel() {
      return this.$t(
        this.hasRepairerEstimateAttachment
          ? 'ESTIMATE_REPAIRER.ATTACHMENT_UPDATE'
          : 'ESTIMATE_REPAIRER.ATTACHMENT_ACTION'
      )
    },
    footerState() {
      return {
        disabled: this.loading || !this.formValid || !this.hasRepairerEstimateAttachment,
        loading: this.loading
      }
    }
  },
  created() {
    if (this.currentRepairFile?.repairer_support_number) {
      this.repairerNumber = this.currentRepairFile.repairer_support_number
    }
  },
  mounted() {
    this.emitFooterState()
  },
  watch: {
    footerState: {
      handler() {
        this.emitFooterState()
      },
      deep: true
    },
    repairerNumber(repairerNumber) {
      this.$emit('repairerNumber', repairerNumber)
    }
  },
  methods: {
    emitFooterState() {
      this.$emit('updateFooterState', this.footerState)
    },
    async openRepairerEstimateAttachmentModal() {
      if (this.attachmentModalPending) return

      this.attachmentModalPending = true
      try {
        await displayModal({
          component: AttachmentUploadModal,
          maxWidth: 700,
          persistent: true,
          props: {
            adapter: createRepairProofUploadAdapter(
              this.currentRepairFile,
              REPAIRER_ESTIMATE_ATTACHMENT_TYPE
            ),
            title: this.$t('ESTIMATE_REPAIRER.ATTACHMENT_MODAL_TITLE')
          }
        })
        this.$emit('updateCurrentRepairFile')
      } finally {
        this.attachmentModalPending = false
      }
    },
    async submit() {
      if (!this.formValid || !this.hasRepairerEstimateAttachment) return

      this.loading = true
      try {
        if (this.currentRepairFile?.repairer_estimate) {
          await this.updateRepairerEstimate()
        } else {
          await this.createRepairerEstimate()
        }
        this.$emit('nextStepEstimateInOffer')
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      } finally {
        this.loading = false
      }
    },
    setError(key) {
      if (key === 'invalid_repairer_repair_file_number_error_key') {
        this.error = { repairerNumber: this.$t('ERROR.' + ResponseConstant[key]) }
        return
      }
      this.error = ''
    },
    createRepairerEstimate() {
      return RepairFileService.createRepairFileRepairerEstimate({
        repairer_number: this.repairerNumber,
        repair_file_id: this.currentRepairFile.technical_id
      })
    },
    updateRepairerEstimate() {
      return RepairFileService.updateRepairFileRepairerEstimate({
        repairer_number: this.repairerNumber,
        repair_file_id: this.currentRepairFile.technical_id,
        repairer_estimate_id: this.currentRepairFile.repairer_estimate.technical_id
      })
    }
  }
}
</script>

<style scoped>
.uploaded-chip {
  max-width: 100%;
}
</style>
